import { SpriteRecovery } from '../spritesheets/SpriteRecovery';
import { withReload } from '../../lib/chunks';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { Download, FolderKanban, MoreHorizontal } from 'lucide-react';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { api, download, ApiError } from '../../lib/api';
import { showUpgradePrompt } from '../plans/UpgradePrompt';
import type { ConversationView, Message, Project } from '../../types/api';
import { Composer, type SendPayload } from './Composer';
import { AssistantMessage, UserMessage, type MessageActions } from './MessageView';
import { useRun } from './useRun';
import { ErrorNote, IconButton, Spinner } from '../../components/ui';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Editors and preview tooling load only when an artifact is opened.
const ArtifactPanel = lazy(withReload(() => import('../artifacts/ArtifactPanel').then((m) => ({ default: m.ArtifactPanel }))));

export function ChatPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const artifactId = params.get('artifact');
  const qc = useQueryClient();
  const nav = useNavigate();
  const wide = useMediaQuery('(min-width: 900px)');
  const view = useQuery({ queryKey: ['conversation', id], queryFn: () => api<ConversationView>(`/api/conversations/${id}`), enabled: !!id });
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api<{ projects: Project[] }>('/api/projects').then((r) => r.projects) });
  const openArtifact = (aid: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (aid) n.set('artifact', aid); else n.delete('artifact'); return n; }, { replace: true });
  const { live, attach, cancel } = useRun(id, { onArtifact: (a) => { if (a.action === 'created' || !params.get('artifact')) openArtifact(a.artifactId); } });
  const [error, setError] = useState<unknown>(null);
  const [lastMode, setLastMode] = useState<SendPayload | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  // Reattach to a run that is still going (after reload or navigation).
  useEffect(() => {
    const active = view.data?.activeRuns?.[0];
    if (active && (!live || live.runId !== active.id)) attach(active.id, { assistantMessageId: active.message_id, userMessageId: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.data?.activeRuns?.[0]?.id]);

  // Start a run handed over from the home page.
  useEffect(() => {
    const pending = sessionStorage.getItem(`lumnos.pendingRun.${id}`);
    if (pending) {
      sessionStorage.removeItem(`lumnos.pendingRun.${id}`);
      const r = JSON.parse(pending) as { runId: string; assistantMessageId: string; userMessageId: string };
      attach(r.runId, r);
    }
  }, [id, attach]);

  useEffect(() => { if (stick.current) bottom.current?.scrollIntoView({ block: 'end' }); });

  const running = !!live && !live.done;
  const send = async (p: SendPayload, parentId?: string | null) => {
    setError(null);
    setLastMode(p);
    try {
      const r = await api<{ runId: string; assistantMessageId: string; userMessageId: string }>(`/api/conversations/${id}/messages`, { body: { requestId: globalThis.crypto?.randomUUID?.(), text: p.text, attachmentIds: p.attachmentIds, mode: p.mode, model: p.model || undefined, thinking: p.thinking, webSearch: p.webSearch, ...(parentId !== undefined ? { parentId } : {}) } });
      stick.current = true;
      attach(r.runId, r);
      void qc.invalidateQueries({ queryKey: ['account'] });
      void qc.invalidateQueries({ queryKey: ['conversation', id] });
    } catch (e) { setError(e); if (e instanceof ApiError && e.code === 'out_of_credits') showUpgradePrompt(); throw e; }
  };

  const actions: MessageActions = {
    onEdit: (m, text) => void send({ ...(lastMode ?? defaults()), text, attachmentIds: m.blocks.filter((b) => b.type === 'attachment').map((b) => b.data.fileId) }, m.parentId),
    onRegenerate: async (m: Message) => {
      setError(null);
      try {
        const p = lastMode ?? defaults();
        const r = await api<{ runId: string; assistantMessageId: string; userMessageId: string }>(`/api/conversations/${id}/regenerate`, { body: { requestId: globalThis.crypto?.randomUUID?.(), assistantMessageId: m.id, mode: p.mode, model: p.model || undefined, thinking: p.thinking, webSearch: p.webSearch } });
        attach(r.runId, r);
        void qc.invalidateQueries({ queryKey: ['conversation', id] });
      } catch (e) { setError(e); if (e instanceof ApiError && e.code === 'out_of_credits') showUpgradePrompt(); }
    },
    onContinue: () => void send({ ...(lastMode ?? defaults()), text: 'Continue from where you left off.', attachmentIds: [] }),
    onAnswer: (text) => void send({ ...(lastMode ?? defaults()), text, attachmentIds: [] }),
    onBranch: async (messageId) => {
      const v = await api<ConversationView>(`/api/conversations/${id}/branch`, { body: { messageId } });
      qc.setQueryData(['conversation', id], { ...v, activeRuns: [] });
    },
  };

  if (view.isLoading) return <div className="center-fill"><Spinner size={22} /></div>;
  if (view.error) return <div className="page"><ErrorNote error={view.error} /><Link to="/">Start a new chat</Link></div>;
  const conv = view.data!.conversation;
  const messages = view.data!.messages;
  const project = projects.data?.find((p) => p.id === conv.projectId);

  const chat = (
    <div className="chat">
      <header className="chat__head">
        <div className="chat__title">
          {project && <Link to={`/projects/${project.id}`} className="crumb"><FolderKanban size={14} />{project.name}</Link>}
          <h1 title={conv.title}>{conv.title}</h1>
        </div>
        <Menu.Root>
          <Menu.Trigger asChild><IconButton label="Conversation options"><MoreHorizontal size={18} /></IconButton></Menu.Trigger>
          <Menu.Portal>
            <Menu.Content className="menu" align="end" sideOffset={4}>
              <Menu.Item className="menu__item" onSelect={() => void download(`/api/conversations/${id}/export?format=md`, 'conversation.md')}><Download size={14} /> Export as Markdown</Menu.Item>
              <Menu.Item className="menu__item" onSelect={() => void download(`/api/conversations/${id}/export?format=json`, 'conversation.json')}><Download size={14} /> Export as JSON</Menu.Item>
              <Menu.Separator className="menu__sep" />
              <Menu.Label className="menu__label">Move to project</Menu.Label>
              {projects.data?.map((p) => (
                <Menu.Item key={p.id} className="menu__item" onSelect={() => void api(`/api/conversations/${id}`, { method: 'PATCH', body: { projectId: p.id } }).then(() => qc.invalidateQueries({ queryKey: ['conversation', id] }))}><FolderKanban size={14} /> {p.name}</Menu.Item>
              ))}
              {conv.projectId && <Menu.Item className="menu__item" onSelect={() => void api(`/api/conversations/${id}`, { method: 'PATCH', body: { projectId: null } }).then(() => qc.invalidateQueries({ queryKey: ['conversation', id] }))}>Remove from project</Menu.Item>}
              <Menu.Separator className="menu__sep" />
              <Menu.Item className="menu__item menu__item--danger" onSelect={() => void api(`/api/conversations/${id}`, { method: 'DELETE' }).then(() => { void qc.invalidateQueries({ queryKey: ['conversations'] }); nav('/'); })}>Delete conversation</Menu.Item>
            </Menu.Content>
          </Menu.Portal>
        </Menu.Root>
      </header>
      <div className="chat__scroll" ref={scroller} onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}>
        <div className="chat__messages">
          {messages.map((m, i) => m.role === 'user'
            ? <UserMessage key={m.id} m={m} actions={actions} busy={running} />
            : <AssistantMessage key={m.id} m={m} live={live} actions={actions} isLast={i === messages.length - 1} busy={running} />)}
          {live && !live.done && !messages.some((m) => m.id === live.assistantMessageId) && (
            <div className="msg msg--assistant"><div className="run-status"><span className="pulse" />Starting…</div></div>
          )}
          <SpriteRecovery conversationId={id!} running={running} />
          <ErrorNote error={error} />
          <div ref={bottom} />
        </div>
      </div>
      <div className="chat__composer">
        <Composer draftKey={id!} conversationId={id} projectId={conv.projectId} running={running} onStop={() => void cancel()} onSend={(p) => send(p)} autoFocus />
        <p className="chat__disclaimer">Lumnos can make mistakes. Check important information.</p>
      </div>
    </div>
  );

  if (!artifactId) return chat;
  const panel = (
    <Suspense fallback={<div className="center-fill"><Spinner /></div>}>
      <ArtifactPanel artifactId={artifactId} conversationId={id} onClose={() => openArtifact(null)} onFix={(t) => void send({ ...(lastMode ?? defaults()), text: t, attachmentIds: [] })} running={running} />
    </Suspense>
  );
  if (!wide) return <div className="artifact-overlay">{panel}</div>;
  return (
    <Group orientation="horizontal" className="split">
      <Panel defaultSize="45%" minSize="28%">{chat}</Panel>
      <Separator className="split__handle" />
      <Panel defaultSize="55%" minSize="30%">{panel}</Panel>
    </Group>
  );
}

function defaults(): SendPayload {
  const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
  return { text: '', attachmentIds: [], mode: (get('lumnos.mode') as any) || 'chat', model: get('lumnos.model') ?? '', thinking: get('lumnos.thinking') === '1', webSearch: get('lumnos.web') !== '0' };
}
